import React from 'react';
import AffilizzCta from './affilizz-cta';
import { useTranslation } from '../../utils/translation';
import { ExternalLink } from '../../atoms/link';
import AffilizzBox from './affilizz-box';
import type { EmbedPluginProps } from './embed';

interface AffilizzSpecialPrice {
  price?: string;
  name?: string;
  dropValue?: string;
  dropPercentage?: string;
}

interface AffilizzOfferProps {
  price?: string;
  offerId?: string;
  currency?: string;
  shopName?: string;
  shopIcon?: string;
  url?: string;
  stock?: boolean;
  marketplace?: boolean;
  condition?: string;
  promoCode?: string;
  specialPrice?: AffilizzSpecialPrice;
}

interface AffilizzTabProps {
  title?: string;
  productImage?: string;
  productName?: string;
  offers?: AffilizzOfferProps[];
}

interface MixedAffilizzProps {
  // eslint-disable-next-line react/no-unused-prop-types
  '@type': string;
}

export interface EmbedAffilizzCtaProps extends MixedAffilizzProps {
  url?: string;
  shopName?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  productName?: string;
  title?: string;
  price?: string;
  promoCode?: string;
  specialPrice?: AffilizzSpecialPrice;
  eventCta?: (shopName?: string, price?: string, position?: string, label?: string) => void;
}

export interface EmbedAffilizzBoxProps extends MixedAffilizzProps {
  title?: string;
  tabs: AffilizzTabProps[];
  eventCta?: (shopName?: string, price?: string, position?: string, label?: string) => void;
}

interface DisplayType {
  handles: (embedAffilizz: MixedAffilizzProps) => boolean;
  Component: React.FC<EmbedAffilizzBoxProps | EmbedAffilizzCtaProps>;
}

const sanitizePrice = (price?: string): string => {
  if (!price) {
    return '';
  }

  const patternReplace = /\./;

  return price.toString().replace(patternReplace, ',');
};

const EmbedAffilizzCta: React.FC<EmbedAffilizzCtaProps> = (props) => {
  const { url, title, shopName, price, promoCode, specialPrice, eventCta } = props;
  const t = useTranslation();
  const sanitizedPrice = sanitizePrice(specialPrice?.price || price);

  const buttonLabel = title || t('design.embed.affilizz.cta.label', { shopName, price: sanitizedPrice });
  // (shopName?: string, price?: string, position?: string, label?: string)
  if (specialPrice && specialPrice.price) {
    return (
      <AffilizzCta
        promoCode={t('design.embed.affilizz.cta.promoCodePrime')}
        url={url}
        buttonLabel={buttonLabel}
        onClick={() => eventCta(shopName, sanitizedPrice, 'affilizz', buttonLabel)}
        as={ExternalLink}
      />
    );
  }

  if (price) {
    return (
      <AffilizzCta
        promoCode={promoCode}
        url={url}
        buttonLabel={buttonLabel}
        as={ExternalLink}
        onClick={() => eventCta(shopName, sanitizedPrice, 'affilizz', buttonLabel)}
      />
    );
  }

  if (title) {
    return (
      <AffilizzCta
        promoCode={promoCode}
        url={url}
        buttonLabel={buttonLabel}
        as={ExternalLink}
        onClick={() => eventCta(shopName, sanitizedPrice, 'affilizz', buttonLabel)}
      />
    );
  }

  return null;
};

const EmbedAffilizzBox: React.FC<EmbedAffilizzBoxProps> = (props) => {
  const { tabs, eventCta, title } = props;
  if (tabs?.length) {
    tabs.forEach((tab): void => {
      if (title && title !== '' && !tab?.title) {
        tab.title = title;
      }
      if (tab?.offers?.length) {
        tab.offers.forEach((offer): void => {
          if (offer.specialPrice && offer.specialPrice.price) {
            offer.price = offer.specialPrice.price;
          }
        });
      }
    });

    return <AffilizzBox title={title} tabs={tabs} eventCta={eventCta} />;
  }

  return null;
};

const displayTypes: DisplayType[] = [
  {
    handles: (embedAffilizz: MixedAffilizzProps) => {
      return embedAffilizz['@type'] === 'CTA';
    },
    Component: EmbedAffilizzCta,
  },
  {
    handles: (embedAffilizz: MixedAffilizzProps) => {
      return embedAffilizz['@type'] === 'BOX';
    },
    Component: EmbedAffilizzBox,
  },
];

const AffilizzEmbed: React.FC<EmbedPluginProps> = (props) => {
  // testing prod IDS 08/2024 :
  // box : 66b38b393535a053ce2931f4
  // cta : 66c4563ec887c36a6e0c4540
  const { embedAffilizz, eventCta } = props;
  if (embedAffilizz) {
    const affilizzDisplay = displayTypes.find((display) => display.handles(embedAffilizz));
    if (affilizzDisplay) {
      return <affilizzDisplay.Component {...embedAffilizz} eventCta={eventCta} />;
    }
  }

  return null;
};

export default AffilizzEmbed;
